"use client"

import { useState } from "react"
import { Task, TaskStatus } from "@/schema"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"
import { Users, X } from "lucide-react"
import { toast } from "sonner"

import { useUpdateTask } from "@/hooks/api/tasks"
import { useFetchTeams } from "@/hooks/api/teams"
import { useFetchUsers } from "@/hooks/api/users"
import { AssignedTo } from "@/components/users/assigned-to"
import { UserAvatar } from "@/components/users/user-avatar"

interface AssignedToCellProps {
  task: Task
}

export function AssignedToCell({ task }: AssignedToCellProps) {
  const [showAssigneeSelector, setShowAssigneeSelector] = useState(false)
  const updateTaskMutation = useUpdateTask()
  const { data: usersData, isLoading: isLoadingUsers } = useFetchUsers()
  const { data: teamsData, isLoading: isLoadingTeams } = useFetchTeams()
  const isCompleted = task.status === TaskStatus.DONE || task.optimistic_status === TaskStatus.DONE

  const users = usersData?.users ?? []
  const teams = teamsData?.teams ?? []
  const hasAssignment = task.assigned_to_id || task.assigned_team_id

  const updateTask = (payload: Partial<Task> & { id: string }) => {
    updateTaskMutation.mutate(payload, {
      onSuccess: () => {
        toast.success("Task updated")
      },
    })
  }

  return (
    <div onClick={(e) => e.stopPropagation()} className="flex cursor-default justify-start">
      <Popover open={showAssigneeSelector} onOpenChange={setShowAssigneeSelector}>
        <PopoverTrigger asChild>
          <div
            className={`flex gap-1 ${isCompleted ? "cursor-default opacity-50" : "cursor-pointer"}`}
            onClick={(e) => {
              if (isCompleted) {
                e.preventDefault()
                return
              }
              setShowAssigneeSelector(true)
            }}
          >
            {task.assigned_team ? (
              <div className="bg-background text-foreground inline-flex min-w-0 items-center gap-1.5 rounded-full border pr-2 text-sm font-medium">
                <div
                  className="flex size-6 items-center justify-center rounded-full"
                  style={{ backgroundColor: task.assigned_team.color || undefined }}
                >
                  <Users className="size-3.5 text-white" />
                </div>
                <span className="truncate">{task.assigned_team.name}</span>
              </div>
            ) : (
              <AssignedTo userId={task.assigned_to_id} />
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[250px] p-0" align="end">
          <Command>
            <CommandInput placeholder="Search users or teams..." />
            <CommandList className="assignee-scroll-area max-h-[300px] w-full overflow-auto">
              <CommandEmpty>No results found.</CommandEmpty>

              {(isLoadingUsers || isLoadingTeams) && <CommandItem disabled>Loading...</CommandItem>}

              {users.length > 0 && (
                <CommandGroup heading="Users">
                  {users.map((user) => (
                    <CommandItem
                      key={user.id}
                      value={user.full_name || user.id}
                      onSelect={() => {
                        updateTask({ id: task.id!, assigned_to_id: user.id, assigned_team_id: null })
                        setShowAssigneeSelector(false)
                      }}
                      onWheel={(e) => e.stopPropagation()}
                    >
                      <UserAvatar userId={user.id} size="sm" showName />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {teams.length > 0 && (
                <CommandGroup heading="Teams">
                  {teams.map((team) => (
                    <CommandItem
                      key={team.id}
                      value={team.name}
                      onSelect={() => {
                        const teamMemberIds = team.users?.map((u) => u.id) ?? []
                        const existingCollaboratorIds = task.collaborator_ids ?? []
                        const mergedCollaboratorIds = Array.from(
                          new Set([...existingCollaboratorIds, ...teamMemberIds])
                        )

                        updateTask({
                          id: task.id!,
                          assigned_team_id: team.id,
                          assigned_to_id: null,
                          collaborator_ids: mergedCollaboratorIds,
                        })
                        setShowAssigneeSelector(false)
                      }}
                      onWheel={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="flex size-5 items-center justify-center rounded-full"
                          style={{ backgroundColor: team.color || undefined }}
                        >
                          <Users className="size-3 text-white" />
                        </div>
                        <span>{team.name}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {hasAssignment && (
                <CommandGroup>
                  <CommandItem
                    value="__remove__"
                    onSelect={() => {
                      updateTask({ id: task.id!, assigned_to_id: null, assigned_team_id: null })
                      setShowAssigneeSelector(false)
                    }}
                  >
                    <X />
                    Remove assignee
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
